import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Box,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  TableSortLabel,
  Paper,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Autocomplete,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { AddCircleOutline, DragHandle, Search, Clear } from '@mui/icons-material';
import CopyToClipboardText from '../ui-components/CopyToClipboardText';

import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { v4 as uuidv4 } from 'uuid';
import CodeMirror from '@uiw/react-codemirror';
import { json } from "@codemirror/lang-json";

const codemirrorExtensions = [json()];
const codemirrorOptions = {
  mode: "application/json",
  theme: "default",
  lineNumbers: true,
};

// 🏗️ CodeMirror Wrapper to prevent cursor jump
const LocalCodeMirror = ({ initialValue, onChange }) => {
  const [localValue, setLocalValue] = useState(initialValue);

  const handleChange = (val) => {
    setLocalValue(val);
    onChange(val);
  };

  return (
    <CodeMirror
      value={localValue}
      extensions={codemirrorExtensions}
      onChange={handleChange}
      options={codemirrorOptions}
    />
  );
};

// 🏗️ Draggable Item Component
const DraggableItem = ({
  entry,
  index,
  proxies,
  handleScreenshotServiceChange,
  handleRemoveScreenshotService,
  screenshotServices,
  handleTestScreenshot,
  isTesting,
  isAnyTestRunning,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: entry.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "10px",
    border: "1px solid lightgray",
    borderRadius: "5px",
    backgroundColor: "#f8f9fa",
    width: "100%",
  };




  return (
    <Box ref={setNodeRef} style={style} {...attributes}>
      <Box style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
        {/* Drag Handle */}
        <IconButton {...listeners}>
          <DragHandle />
        </IconButton>

        {/* Proxy Selector */}
        <FormControl fullWidth>
          <InputLabel id={`proxy-label-${entry.id}`} shrink>Proxy</InputLabel>
          <Select
            labelId={`proxy-label-${entry.id}`}
            value={entry.ProxyID || ""}
            label="Proxy"
            displayEmpty
            onChange={(e) => handleScreenshotServiceChange(index, "ProxyID", e.target.value)}
            onPointerDownCapture={(e) => e.stopPropagation()} // Prevents drag interference
          >
            <MenuItem value="">No Proxy</MenuItem>
            {proxies.map((proxy) => (
              <MenuItem key={proxy.ProxyID} value={proxy.ProxyID}>
                {proxy.ProxyName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Screenshot Service Selector */}
        <FormControl fullWidth>
          <InputLabel>Screenshot Service</InputLabel>
          <Select
            value={entry.ServiceID || ""}
            onChange={(e) => handleScreenshotServiceChange(index, "ServiceID", e.target.value)}
            onPointerDownCapture={(e) => e.stopPropagation()} // Prevents drag interference
          >
            {screenshotServices.map((s) => (
              <MenuItem key={s.ScreenshotServiceID} value={s.ScreenshotServiceID}>
                {s.serviceName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Edit JSON Button */}
        <Button
          onPointerDownCapture={(e) => e.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleScreenshotServiceChange(index, "isEditing", !entry.isEditing);
          }}
        >
          {entry.isEditing ? "Close Editor" : "Edit JSON"}
        </Button>

        {/* 🔹 Test Screenshot Button */}
        <Button
          variant="contained"
          color="primary"
          onPointerDownCapture={(e) => e.stopPropagation()} // Prevents dnd-kit interference
          onClick={() => handleTestScreenshot(entry)}
          disabled={isTesting || isAnyTestRunning}
          style={{ width: "200px" }}
        >
          {isTesting ? "Testing..." : "Test"}
        </Button>

        {/* Remove Button */}
        <Button
          color="error"
          onPointerDownCapture={(e) => e.stopPropagation()} // 🛑 Prevent drag interference
          onClick={() => {
            handleRemoveScreenshotService(entry.id);
          }}
        >
          ➖
        </Button>
      </Box>

      {/* JSON Editor (Visible When Expanded) */}
      {entry.isEditing && (
        <Box style={{ width: "100%", marginTop: "10px" }}>
          <LocalCodeMirror
            initialValue={entry.Config}
            onChange={(value) => handleScreenshotServiceChange(index, "Config", value)}
          />

          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f4f6f8', borderRadius: 1, border: '1px dashed #ccc' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>Useful URLBox Parameters (Copy & Paste):</Typography>
            
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5, fontWeight: 'bold', fontSize: '0.75rem' }}>1. Bypass Cookie Banners & Delay Capture:</Typography>
            <Box component="pre" sx={{ bgcolor: '#fff', p: 1, border: '1px solid #ddd', borderRadius: 0.5, fontSize: '0.72rem', overflowX: 'auto', my: 0.5, fontFamily: 'monospace' }}>
{`{
  "user_agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36",
  "hide_cookie_banners": true,
  "click_accept": true,
  "delay": 5000
}`}
            </Box>

            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1, fontWeight: 'bold', fontSize: '0.75rem' }}>2. Wait for Element & Auto Click Selector:</Typography>
            <Box component="pre" sx={{ bgcolor: '#fff', p: 1, border: '1px solid #ddd', borderRadius: 0.5, fontSize: '0.72rem', overflowX: 'auto', my: 0.5, fontFamily: 'monospace' }}>
{`{
  "wait_for": "#lobby-container",
  "click": "#accept-cookies",
  "block_ads": true
}`}
            </Box>

            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1, fontWeight: 'bold', fontSize: '0.75rem' }}>3. Execute Custom Javascript Before Capture:</Typography>
            <Box component="pre" sx={{ bgcolor: '#fff', p: 1, border: '1px solid #ddd', borderRadius: 0.5, fontSize: '0.72rem', overflowX: 'auto', my: 0.5, fontFamily: 'monospace' }}>
{`{
  "js": "setTimeout(() => { document.querySelector('#dismiss-btn')?.click() }, 1000);"
}`}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};






const normalizeTag = (tag) => {
  if (!tag) return "";
  return tag.toLowerCase().trim().replace(/\s+/g, '-');
};

const WebsitesConfiguration = () => {
  const [websites, setWebsites] = useState([]);
  const [proxies, setProxies] = useState([]); // Store available proxies
  const [open, setOpen] = useState(false);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [brands, setBrands] = useState([]);
  const [screenshotServices, setScreenshotServices] = useState([]);
  const [formState, setFormState] = useState({
    WebsiteName: '',
    scanURL: '',
    Tags: [],
    ScreenshotFailbackOrder: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState({});
  const [isAnyTestRunning, setIsAnyTestRunning] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);

  // Delete Dialog State
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter State
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'paused'
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('WebsiteName'); // 'WebsiteName', 'Region', 'Country', 'Paused'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc', 'desc'
  const [availableTags, setAvailableTags] = useState([]);
  const [tagInputValue, setTagInputValue] = useState('');
  const [hideTags, setHideTags] = useState(false);



  useEffect(() => {
    fetchWebsiteCatalog();
    fetchProxies(); // ✅ Fetch available proxies on page load
    fetchScreenshotServices();
  }, []);

  const fetchWebsiteCatalog = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/websites/catalog`, {
        method: 'GET',
        headers: { 'x-api-key': process.env.REACT_APP_API_KEY },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Websites are already sorted by the backend potentially, but let's be sure
      const sortedWebsites = data.websites.sort((a, b) => a.WebsiteName.localeCompare(b.WebsiteName));
      setWebsites(sortedWebsites);

      // Brands are already sorted by the backend in the new version
      setBrands(data.filters.brands);
      setAvailableTags(data.filters.tags || []);

    } catch (error) {
      console.error('Error fetching website catalog:', error);
    }
  };

  const saveWebsite = async (websiteData) => {
    const isUpdating = Boolean(websiteData.WebsitesDBID); // If ID exists, it's an update

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/websites${isUpdating ? `/${websiteData.WebsitesDBID}` : ""}`,
      {
        method: isUpdating ? "PATCH" : "POST", // ✅ POST for new, PATCH for updates
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY
        },
        body: JSON.stringify(websiteData),
      }
    );

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
    await response.json();
    return true;
  };



  const fetchScreenshotServices = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/screenshot-services`, {
        method: 'GET',
        headers: { 'x-api-key': process.env.REACT_APP_API_KEY },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setScreenshotServices(data);
    } catch (error) {
      console.error('Error fetching screenshot services:', error);
    }
  };


  const fetchProxies = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/proxys`, {
        method: 'GET',
        headers: { 'x-api-key': process.env.REACT_APP_API_KEY },
      });
      const data = await response.json();
      // Map to expected keys
      const formattedProxies = data.map(proxy => ({
        ProxyID: proxy.id,      // Fix: Use correct ID field
        ProxyName: proxy.name,  // Fix: Use correct name field
      }));
      setProxies(formattedProxies);
    } catch (error) {
      console.error('Error fetching proxies:', error);
    }
  };




  const handleOpen = (website = null) => {
    setSelectedWebsite(website);

    const updatedFailbackOrder = website
      ? website.ScreenshotFailbackOrder.map((entry, index) => ({
        ...entry,
        id: entry.id || `${entry.ServiceID}-${index}`,
        isEditing: entry.isEditing ?? false,
      }))
      : [];

    const sanitizedWebsite = website ? { ...website } : {};
    if (sanitizedWebsite) {
      sanitizedWebsite.BrandID = sanitizedWebsite.BrandID || sanitizedWebsite.brandId || sanitizedWebsite.brand_id || "";
      delete sanitizedWebsite.Country;
      delete sanitizedWebsite.Region;
      delete sanitizedWebsite.country;
      delete sanitizedWebsite.region;
    }

    setFormState(
      website
        ? { ...sanitizedWebsite, ScreenshotFailbackOrder: updatedFailbackOrder, Paused: website.Paused || false, Tags: website.Tags || [] }
        : { WebsiteName: '', scanURL: '', ScreenshotFailbackOrder: [], WebsitesDBID: uuidv4(), Paused: false, BrandID: "", Tags: [] }
    );
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedWebsite(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleAddScreenshotService = () => {
    setFormState({
      ...formState,
      ScreenshotFailbackOrder: [
        ...formState.ScreenshotFailbackOrder,
        { id: uuidv4(), ProxyID: "", ServiceID: "", Config: "{}", isEditing: false }, // ✅ Track editing state
      ],
    });
  };

  const handleRemoveScreenshotService = (entryId) => {
    const updatedFailbackOrder = formState.ScreenshotFailbackOrder.filter(
      (entry) => entry.id !== entryId
    );
    setFormState({ ...formState, ScreenshotFailbackOrder: updatedFailbackOrder });
  };



  const handleScreenshotServiceChange = (index, field, value) => {
    setFormState((prevState) => {
      const updatedServices = prevState.ScreenshotFailbackOrder.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry
      );
      return { ...prevState, ScreenshotFailbackOrder: updatedServices };
    });
  };

  const parseConfigOrNull = (config) => {
    try {
      const source = config && config.trim().length ? config : '{}';
      return JSON.parse(source);
    } catch (err) {
      return null;
    }
  };

  const validateScreenshotConfigs = () => {
    for (let i = 0; i < formState.ScreenshotFailbackOrder.length; i += 1) {
      const entry = formState.ScreenshotFailbackOrder[i];
      if (!parseConfigOrNull(entry.Config)) {
        alert(`Invalid JSON in screenshot configuration #${i + 1}. Please fix it before continuing.`);
        return false;
      }
    }
    return true;
  };

  const handleTestScreenshot = async (entry) => {
    if (!formState || !formState.scanURL) {
      alert("❌ Website URL is missing. Cannot test screenshot.");
      return;
    }
    if (isAnyTestRunning) {
      alert("A test is already running. Please wait for it to complete.");
      return;
    }

    // Set testing states
    setIsTesting((prev) => ({ ...prev, [entry.id]: true }));
    setIsAnyTestRunning(true); // ✅ Disable all tests

    setScreenshotUrl(null); // Clear old screenshot


    const parsedConfig = parseConfigOrNull(entry.Config);
    if (!parsedConfig) {
      setIsTesting((prevState) => ({ ...prevState, [entry.id]: false }));
      setIsAnyTestRunning(false);
      alert("Invalid JSON in Config field. Fix it before testing.");
      return;
    }

    const payload = {
      scanURL: formState.scanURL,
      ServiceID: entry.ServiceID,
      ProxyID: entry.ProxyID,
      Config: JSON.stringify(parsedConfig),
    };

    // ✅ Set button to "Testing..."
    setIsTesting((prev) => ({ ...prev, [entry.id]: true }));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/test-screenshot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Test screenshot failed.");
      }

      // ✅ Start polling for screenshot availability
      await pollForScreenshot(Date.now(), entry.id);

    } catch (error) {
      console.error("❌ Error testing screenshot:", error);
      alert(`Error testing screenshot: ${error.message}`);
    } finally {
      setIsTesting((prev) => ({ ...prev, [entry.id]: false }));
      setIsAnyTestRunning(false); // ✅ Re-enable tests
    }
  };



  const pollForScreenshot = async (testStartTime, entryId) => {
    const MAX_WAIT_TIME = 120000; // 2 minutes in milliseconds
    const POLL_INTERVAL = 5000; // 5 seconds per poll
    const endTime = Date.now() + MAX_WAIT_TIME;

    while (Date.now() < endTime) {
      try {
        // ✅ Call our actual API `/test-screenshot/status`
        const response = await fetch(`${process.env.REACT_APP_API_URL}/test-screenshot/status`, {
          method: "GET",
          headers: {
            "x-api-key": process.env.REACT_APP_API_KEY,
          },
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Error fetching screenshot status.");
        }

        const lastModified = new Date(data.lastModified);

        // If file is new, show it
        if (lastModified > testStartTime) {
          setScreenshotUrl(data.signedUrl);
          return;
        }

      } catch (error) {
        console.error("⚠️ Polling error:", error);
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
    }

    // Timeout reached - no new screenshot found
    setIsTesting((prev) => ({ ...prev, [entryId]: false })); // ✅ Reset button state on failure
    alert("Screenshot test failed. No new image was detected within 2 minutes.");


  };

  const handleOpenDelete = (website) => {
    setWebsiteToDelete(website);
    setOpenDeleteDialog(true);
  };

  const handleCloseDelete = () => {
    setOpenDeleteDialog(false);
    setWebsiteToDelete(null);
  };

  const handleDeleteCallback = async () => {
    if (!websiteToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/websites/${websiteToDelete.WebsitesDBID}`, {
        method: 'DELETE',
        headers: { 'x-api-key': process.env.REACT_APP_API_KEY },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete website: ${response.status}`);
      }

      // Remove from state
      setWebsites((prev) => prev.filter((w) => w.WebsitesDBID !== websiteToDelete.WebsitesDBID));
      handleCloseDelete();
    } catch (error) {
      console.error('Error deleting website:', error);
      alert('Failed to delete website. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };






  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = formState.ScreenshotFailbackOrder.findIndex((item) => item.id === active.id);
    const newIndex = formState.ScreenshotFailbackOrder.findIndex((item) => item.id === over.id);

    const newOrder = arrayMove(formState.ScreenshotFailbackOrder, oldIndex, newIndex);
    setFormState({ ...formState, ScreenshotFailbackOrder: newOrder });
  };

  // Region & Country Options
  const regions = useMemo(() => {
    const r = new Set();
    let hasNone = false;
    websites.forEach((w) => {
      if (w.Region) r.add(w.Region);
      else hasNone = true;
    });
    const sorted = Array.from(r).sort();
    if (hasNone) sorted.push('Unknown');
    return ['all', ...sorted];
  }, [websites]);

  const countries = useMemo(() => {
    const rFiltered = websites.filter((w) => {
      if (selectedRegion === 'all') return true;
      if (selectedRegion === 'Unknown') return !w.Region;
      return w.Region === selectedRegion;
    });
    const c = new Set();
    let hasNone = false;
    rFiltered.forEach((w) => {
      if (w.Country) c.add(w.Country);
      else hasNone = true;
    });
    const sorted = Array.from(c).sort();
    if (hasNone) sorted.push('Unknown');
    return ['all', ...sorted];
  }, [websites, selectedRegion]);

  const handleRequestSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and Sort logic
  const filteredAndSortedWebsites = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const filtered = websites.filter((website) => {
      if (filterStatus === 'active' && website.Paused) return false;
      if (filterStatus === 'paused' && !website.Paused) return false;

      if (selectedRegion !== 'all') {
        const reg = website.Region;
        if (selectedRegion === 'Unknown') {
          if (reg) return false;
        } else if (reg !== selectedRegion) {
          return false;
        }
      }

      if (selectedCountry !== 'all') {
        const ctry = website.Country;
        if (selectedCountry === 'Unknown') {
          if (ctry) return false;
        } else if (ctry !== selectedCountry) {
          return false;
        }
      }

      if (query) {
        const name = (website.WebsiteName || '').toLowerCase();
        const url = (website.scanURL || website.URL || '').toLowerCase();
        const country = (website.Country || '').toLowerCase();
        const region = (website.Region || '').toLowerCase();
        const id = (website.WebsitesDBID || '').toLowerCase();
        const tags = (website.Tags || []).map((t) => (t || '').toLowerCase());

        const matchesName = name.includes(query);
        const matchesUrl = url.includes(query);
        const matchesCountry = country.includes(query);
        const matchesRegion = region.includes(query);
        const matchesId = id.includes(query);
        const matchesTags = tags.some((t) => t.includes(query));

        if (!matchesName && !matchesUrl && !matchesCountry && !matchesRegion && !matchesId && !matchesTags) {
          return false;
        }
      }

      return true;
    });

    return filtered.sort((a, b) => {
      let comparison = 0;

      if (sortField === 'Region') {
        const aVal = (a.Region || '').toLowerCase();
        const bVal = (b.Region || '').toLowerCase();
        comparison = aVal.localeCompare(bVal);
        if (comparison === 0) {
          comparison = (a.Country || '').toLowerCase().localeCompare((b.Country || '').toLowerCase());
        }
        if (comparison === 0) {
          comparison = (a.WebsiteName || '').toLowerCase().localeCompare((b.WebsiteName || '').toLowerCase());
        }
      } else if (sortField === 'Country') {
        const aVal = (a.Country || '').toLowerCase();
        const bVal = (b.Country || '').toLowerCase();
        comparison = aVal.localeCompare(bVal);
        if (comparison === 0) {
          comparison = (a.Region || '').toLowerCase().localeCompare((b.Region || '').toLowerCase());
        }
        if (comparison === 0) {
          comparison = (a.WebsiteName || '').toLowerCase().localeCompare((b.WebsiteName || '').toLowerCase());
        }
      } else if (sortField === 'Paused') {
        const aVal = a.Paused ? 1 : 0;
        const bVal = b.Paused ? 1 : 0;
        comparison = aVal - bVal;
        if (comparison === 0) {
          comparison = (a.WebsiteName || '').toLowerCase().localeCompare((b.WebsiteName || '').toLowerCase());
        }
      } else {
        const aVal = (a.WebsiteName || '').toLowerCase();
        const bVal = (b.WebsiteName || '').toLowerCase();
        comparison = aVal.localeCompare(bVal);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [websites, filterStatus, selectedRegion, selectedCountry, searchQuery, sortField, sortDirection]);

  return (
    <div>
      <h2>Website Configurations</h2>

      {/* Filter and Search Bar */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', bgcolor: '#f8f9fa' }}>
        <TextField
          size="small"
          placeholder="Search name, URL, tag, location..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ minWidth: 260, flexGrow: 1 }}
        />

        <Autocomplete
          options={regions}
          size="small"
          sx={{ minWidth: 160 }}
          value={selectedRegion}
          getOptionLabel={(option) => (option === 'all' ? 'All Regions' : option)}
          onChange={(_, val) => {
            setSelectedRegion(val || 'all');
            setSelectedCountry('all');
          }}
          renderInput={(params) => <TextField {...params} label="Region" />}
        />

        <Autocomplete
          options={countries}
          size="small"
          sx={{ minWidth: 160 }}
          value={selectedCountry}
          getOptionLabel={(option) => (option === 'all' ? 'All Countries' : option)}
          onChange={(_, val) => setSelectedCountry(val || 'all')}
          renderInput={(params) => <TextField {...params} label="Country" />}
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant={filterStatus === 'all' ? "contained" : "outlined"}
            onClick={() => setFilterStatus('all')}
          >
            All
          </Button>
          <Button
            size="small"
            variant={filterStatus === 'active' ? "contained" : "outlined"}
            onClick={() => setFilterStatus('active')}
            color="success"
          >
            Active
          </Button>
          <Button
            size="small"
            variant={filterStatus === 'paused' ? "contained" : "outlined"}
            onClick={() => setFilterStatus('paused')}
            color="warning"
          >
            Paused
          </Button>
        </Box>

        <FormControlLabel
          control={<Switch checked={hideTags} onChange={(e) => setHideTags(e.target.checked)} size="small" />}
          label="Hide Tags"
          sx={{ ml: 1 }}
        />

        {(searchQuery || selectedRegion !== 'all' || selectedCountry !== 'all' || filterStatus !== 'all') && (
          <Button
            size="small"
            onClick={() => {
              setSearchQuery('');
              setSelectedRegion('all');
              setSelectedCountry('all');
              setFilterStatus('all');
            }}
          >
            Reset Filters
          </Button>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button onClick={() => handleOpen()} variant="contained" color="primary">
          Add Website
        </Button>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredAndSortedWebsites.length} of {websites.length} websites
        </Typography>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={sortField === 'WebsiteName' ? sortDirection : false}>
              <TableSortLabel
                active={sortField === 'WebsiteName'}
                direction={sortField === 'WebsiteName' ? sortDirection : 'asc'}
                onClick={() => handleRequestSort('WebsiteName')}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortField === 'Region' ? sortDirection : false}>
              <TableSortLabel
                active={sortField === 'Region'}
                direction={sortField === 'Region' ? sortDirection : 'asc'}
                onClick={() => handleRequestSort('Region')}
              >
                Region
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortField === 'Country' ? sortDirection : false}>
              <TableSortLabel
                active={sortField === 'Country'}
                direction={sortField === 'Country' ? sortDirection : 'asc'}
                onClick={() => handleRequestSort('Country')}
              >
                Country
              </TableSortLabel>
            </TableCell>
            <TableCell>URL</TableCell>
            {!hideTags && <TableCell>Tags</TableCell>}
            <TableCell sortDirection={sortField === 'Paused' ? sortDirection : false}>
              <TableSortLabel
                active={sortField === 'Paused'}
                direction={sortField === 'Paused' ? sortDirection : 'asc'}
                onClick={() => handleRequestSort('Paused')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAndSortedWebsites.map((website) => (
            <TableRow key={website.WebsitesDBID || website.WebsiteName} style={{ opacity: website.Paused ? 0.6 : 1 }}>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight="bold">{website.WebsiteName}</Typography>
                  <CopyToClipboardText text={website.WebsitesDBID} />
                </Box>
              </TableCell>
              <TableCell>{website.Region || '-'}</TableCell>
              <TableCell>{website.Country || '-'}</TableCell>
              <TableCell>{website.scanURL}</TableCell>
              {!hideTags && (
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(website.Tags || []).map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                </TableCell>
              )}
              <TableCell>
                {website.Paused ? (
                  <span style={{ color: 'orange', fontWeight: 'bold' }}>⏸️ Paused</span>
                ) : (
                  <span style={{ color: 'green', fontWeight: 'bold' }}>✅ Active</span>
                )}
              </TableCell>
              <TableCell>
                <Button onClick={() => handleOpen(website)}>Edit</Button>
                <Button color="error" onClick={() => handleOpenDelete(website)} style={{ marginLeft: "10px" }}>Delete</Button>
              </TableCell>
            </TableRow>
          ))}
          {filteredAndSortedWebsites.length === 0 && (
            <TableRow>
              <TableCell colSpan={hideTags ? 6 : 7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                No websites match the current filters.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",  // ✅ Set width to 85% of the viewport
            maxWidth: "none"  // ✅ Remove default max width restrictions
          }
        }}
      >
        <DialogTitle>{selectedWebsite ? 'Edit Website' : 'Add Website'}</DialogTitle>
        <DialogContent>
          <TextField name="WebsiteName" label="Name" fullWidth value={formState.WebsiteName} onChange={handleChange} margin="normal" />
          <TextField name="scanURL" label="URL" fullWidth value={formState.scanURL} onChange={handleChange} margin="normal" />

          <Autocomplete
            multiple
            freeSolo
            options={availableTags}
            value={formState.Tags || []}
            inputValue={tagInputValue || ''}
            onInputChange={(event, newInputValue) => {
              setTagInputValue(newInputValue);
            }}
            onChange={(event, newValue) => {
              const normalized = newValue.map(t => normalizeTag(t));
              setFormState({ ...formState, Tags: [...new Set(normalized)] });
              setTagInputValue(''); // Clear input after selection
            }}
            onKeyDown={(e) => {
              // Ensure we don't submit forms when pressing enter inside autocomplete
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Tags"
                placeholder="Type a tag and press Enter"
                margin="normal"
              />
            )}
            sx={{ mt: 2, mb: 1 }}
          />

          <FormControl fullWidth style={{ marginTop: '15px', marginBottom: '15px' }}>
            <InputLabel id="paused-label">Status</InputLabel>
            <Select
              labelId="paused-label"
              value={formState.Paused ? "true" : "false"}
              label="Status"
              onChange={(e) => setFormState({ ...formState, Paused: e.target.value === "true" })}
            >
              <MenuItem value="false">✅ Active</MenuItem>
              <MenuItem value="true">⏸️ Paused</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Brand</InputLabel>
            <Select
              value={formState.BrandID || ""}
              onChange={(e) => setFormState({ ...formState, BrandID: e.target.value })}
            >
              {brands.map((brand) => (
                <MenuItem key={brand.BrandID} value={brand.BrandID}>
                  {brand.BrandName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <h4>Screenshot Failback Order</h4>

          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={formState.ScreenshotFailbackOrder.map((entry) => entry.id)} strategy={verticalListSortingStrategy}>
              {formState.ScreenshotFailbackOrder.map((entry, index) => (
                <DraggableItem
                  key={entry.id}
                  entry={entry}
                  index={index}
                  handleScreenshotServiceChange={handleScreenshotServiceChange}
                  handleRemoveScreenshotService={handleRemoveScreenshotService}
                  proxies={proxies}
                  screenshotServices={screenshotServices}
                  handleTestScreenshot={handleTestScreenshot}
                  isTesting={isTesting[entry.id] || false}
                  isAnyTestRunning={isAnyTestRunning}
                />
              ))}
            </SortableContext>
          </DndContext>

          <Button onClick={handleAddScreenshotService}><AddCircleOutline /> Add Service</Button>

          {/* 🖼️ Display Screenshot if Available */}
          {screenshotUrl && (
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <h3>Test Screenshot Result</h3>
              <img
                src={screenshotUrl}
                alt="Test Screenshot"
                style={{ width: "100%", maxWidth: "800px", border: "1px solid gray" }}
              />
            </div>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={async () => {
              setIsSaving(true); // Start saving
              if (!validateScreenshotConfigs()) {
                setIsSaving(false);
                return;
              }
              
              // Handle pending tag input
              let finalFormState = { ...formState };
              if (tagInputValue.trim() !== '') {
                const newTag = normalizeTag(tagInputValue);
                if (newTag) {
                  finalFormState.Tags = [...new Set([...(finalFormState.Tags || []), newTag])];
                }
              }

              const success = await saveWebsite(finalFormState);

              if (success) {
                fetchWebsiteCatalog(); // ✅ Refresh table after saving
                setTagInputValue(''); // Clear pending input
                handleClose(); // ✅ Close the dialog on success
              } else {
                console.error("❌ Save failed, keeping dialog open.");
              }

              setIsSaving(false); // Stop saving
            }}
            disabled={isSaving} // ✅ Disable button while saving
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🗑️ Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Website?"}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{websiteToDelete?.WebsiteName}</strong>?
          <br /><br />
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteCallback} color="error" autoFocus disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};


export default WebsitesConfiguration;
